import { Profile } from "../../../../core/models/entities/Profile";
import { Organization } from "../../../../core/models/entities/Organization";
import {
  Color,
  LogoOptions,
} from "../../../../core/models/entities/LogoOptions";
import { BaseLogo, ImageLogo } from "../../../../core/models/entities/BaseLogo";
import { JSX, RefObject, useEffect, useRef, useState } from "react";
import { Role } from "../../../../core/models/entities/Role";
import Dropdown from "../../../components/dropdown/Dropdown";
import { extensionsToMimeTypes } from "../componenets/ExtensionsToMimeTypesString";
import {
  ColorChange,
  useLogoActionHandler,
} from "../../logo-list/hooks/useLogoActionsHandler";
import { ListReset } from "../componenets/LogoPicker";
import { useConfirmationDialog } from "../../../components/Dialogs/confirmation-dialog";
import { useApprovalDialogByRole } from "../../../components/Dialogs/useApprovalDialogByRole";
import { useNavigate } from "react-router";
import { ImageLogoForm } from "../models/ImageLogoForm";
import { correctLogoSizeForTransferMethod } from "../../../../utils/functions/SizeUtil";
import { markLogoListAsStale } from "../../../../utils/functions/logo-refresh.util";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../../enviroment";
import { useEditorLogoHeal } from "./useEditorLogoHeal";
import {
  patchFormFromDetail,
  patchLogoFromDetail,
} from "../functions/detailSync";
import {
  canPreviewAsImage,
  UploadPreviewState,
} from "../componenets/UploadPreview";
import {
  hasGenuineHighRes,
  withKeptSharpImage,
  withSharpPlaceholder,
} from "../functions/keepSharp";
import { getHealDelaysMs } from "../../../../hooks/useHealPendingHighRes";
import { useEditorUploadPendingPoll } from "./useEditorUploadPendingPoll";
import { useEditorSharpWaitPoll } from "./useEditorSharpWaitPoll";
import { isSupplierUnavailableError } from "../../../../utils/functions/supplierUnavailable";

// Flags REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW / REACT_APP_LOGO_HEAL_PER_MUTATION
// - build-time constants (hook order never varies at runtime).
const optimisticEnabled = enviroment.logoEditorOptimisticPreviewEnabled;
const healEnabled = enviroment.logoHealPerMutationEnabled;

export interface LogoEditor_VM {
  isLoading: boolean;
  isToolLoading: boolean;
  logoListRef: RefObject<ListReset>;
  options: LogoOptions;
  selectedCustomer?: Organization;
  filters: JSX.Element[];
  onFileUploadUpload: (image: File) => void;
  onSelectLogo: (logo: BaseLogo) => void;
  selectedLogo: BaseLogo | undefined;
  // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: the local file being
  // uploaded (shown in the canvas at once); null when no upload runs.
  uploadPreview: UploadPreviewState | null;
  // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: from -> to pairs of the
  // colour change in flight (chips in the progress strip); null otherwise.
  pendingColorChanges: ColorChange[] | null;
  onToolCancel: () => void;
  onToolSaveDraftChanges?: () => void;
  onToolSaveApprovalChanges: () => void;
  onToolUpdateColor: (colors: ColorChange[]) => void;
  profile: Profile;
  approvalDialog?: JSX.Element;
  confirmationDialog?: JSX.Element;
  customers: Organization[];
  logoForm: ImageLogoForm;
  updateForm: (form: ImageLogoForm) => void;
  onResize: () => void;
}

export function useLogoEditorViewModel(
  profile: Profile,
  customers: Organization[],
  vendors: Organization[],
  options: LogoOptions,
  selectedCustomerId: number | undefined,
  preselectedLogo: BaseLogo | undefined
): LogoEditor_VM {
  const { t } = useTranslation();
  const [selectedCustomer, setSelectedCustomer] = useState<
    Organization | undefined
  >(customers.find((x) => x.id === selectedCustomerId));
  const [selectedLogo, setSelectedLogo] = useState<BaseLogo | undefined>(
    preselectedLogo
  );
  const [uploadImage, setUploadImage] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<UploadPreviewState | null>(
    null
  );
  const [pendingColorChanges, setPendingColorChanges] = useState<
    ColorChange[] | null
  >(null);
  // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: bumped after every mutation so the
  // bounded heal restarts its 3-poll budget ("last mutation wins").
  const [healToken, setHealToken] = useState(0);
  const bumpHeal = () => {
    if (healEnabled) setHealToken((n) => n + 1);
  };
  // Object URL of the file currently previewed; revoked when the upload
  // settles and, as a safety net, on unmount.
  const previewUrlRef = useRef<string | null>(null);
  const releasePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };
  useEffect(() => releasePreviewUrl, []);
  // Flag REACT_APP_LOGO_KEEP_SHARP (F2): the uploaded file stays the canvas
  // bitmap after the upload response (which only carries the 180px icon).
  // Released - object URL revoked - when the logo's genuine high-res lands,
  // when the heal budget is spent without one (the server image wins then),
  // when the artwork changes (colour/method) or when the canvas moves on.
  // Read at call time: process.env is inlined at build, and no hook depends
  // on it. Always null with the flag off.
  const heldUploadRef = useRef<{
    logoId: number;
    pendingPolls: number;
    // The next delivered detail is the mutation response's own cache seed
    // (useEditorLogoHeal hands it over once the mutation settles), not a
    // heal poll - it must not count against the budget.
    absorbSeed: boolean;
  } | null>(null);
  const releaseHeldUpload = () => {
    if (!heldUploadRef.current) return;
    heldUploadRef.current = null;
    releasePreviewUrl();
  };
  // A resize/title save that keeps the hold also restarts the heal budget
  // (bumpHeal): the poll count starts over and the response's own seed,
  // delivered next, is skipped. No hold = no-op.
  const restartHeldUploadBudget = () => {
    const held = heldUploadRef.current;
    if (!held) return;
    held.pendingPolls = 0;
    held.absorbSeed = true;
  };
  // Per delivered heal poll for the held upload: true when the local file
  // must now give way to the server's image (budget spent, still pending).
  const spendHeldUpload = (detail: BaseLogo): boolean => {
    const held = heldUploadRef.current;
    if (!held || held.logoId !== detail.id) return false;
    if (hasGenuineHighRes(detail.displayImage)) {
      releaseHeldUpload();
      return false;
    }
    if (held.absorbSeed) {
      held.absorbSeed = false;
      return false;
    }
    held.pendingPolls += 1;
    if (held.pendingPolls < getHealDelaysMs().length) return false;
    releaseHeldUpload();
    return true;
  };
  const [colorChangeCode, setColorChangeCode] = useState<string | null>(null);
  const [isFullLoading, setIsFullLoading] = useState<boolean>(false);
  const {
    isLoading,
    handleInitiateApproval,
    handleUpdateLogo,
    handleUpdateLogoColor,
    handleResizeLogo,
    handleCreateLogo,
    handleSkipApproval,
    handleSkipAddToCart,
  } = useLogoActionHandler();

  const copy = Object.assign({}, selectedLogo as ImageLogo);
  const [logoForm, setLogoForm] = useState<ImageLogoForm>(toLogoForm(copy));

  // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: subscribe to the open logo's detail
  // entry (already seeded by every mutation - 0 extra GetLogo), poll for the
  // sharp render up to 3x per mutation, and patch ONLY image/size/colours of
  // the open logo when it lands - never the title/method/customer the user
  // may be editing, and never a size field the user has typed in.
  useEditorLogoHeal(selectedLogo, isLoading, healToken, (detail) => {
    const saved = selectedLogo;
    if (!saved || saved.id !== detail.id) return;
    // Flag REACT_APP_LOGO_KEEP_SHARP (F1): a poll without a genuine high-res
    // never downgrades the sharp canvas - except when it is the last poll of
    // an upload's heal budget (F2: the local file then yields to the server).
    const keepSharp =
      enviroment.logoKeepSharpEnabled && !spendHeldUpload(detail);
    setSelectedLogo((prev) =>
      prev ? patchLogoFromDetail(prev, detail, keepSharp) : prev
    );
    setLogoForm((prev) => patchFormFromDetail(prev, saved, detail));
  });

  // Flag REACT_APP_CLONE_INSTANT_ENABLED: while the open logo is a clone
  // whose supplier identity the API's worker still owes, re-read ONLY its
  // own GetLogo entry at 3/8/15 s (max 3 calls) - the heal subscription
  // above delivers the result (patchLogoFromDetail syncs uploadPending /
  // serial), so the badge clears and the locks open without F5. Build-time
  // constant hook (no body with the flag off).
  useEditorUploadPendingPoll(selectedLogo);

  // Flag REACT_APP_CLONE_INSTANT_SHARP_WAIT: while that clone also lacks a
  // genuine high-res, re-read its own GetLogo faster (2/2/3/3/5/5/5/5 s, max
  // 8) so the canvas leaves the natural-size preview as soon as the worker
  // has stored the high-res; ONE extra GetLogo 1 s after the stamp clears
  // without one. The re-read above and the heal stand down meanwhile.
  // Build-time constant hook (no body with the flag off).
  useEditorSharpWaitPoll(selectedLogo);

  const navigate = useNavigate();
  const { component, start } = useApprovalDialogByRole<BaseLogo>({
    role: profile.userOrganizationInformations.role,
    subject: t("logoEditorCore.approvalSubjectLogo"),
    onSkip: (entity) =>
      handleSkipApproval(entity).then(() =>
        navigate("/logo", { state: { updatingLogoId: entity.id } })
      ),
    onOrder: (entity) =>
      handleSkipAddToCart(entity, 10).then(() =>
        navigate("/logo", { state: { updatingLogoId: entity.id } })
      ),
    onInitApproval: (entity, email) =>
      handleInitiateApproval(entity, { skip: false, email }).then(() =>
        navigate("/logo", { state: { updatingLogoId: entity.id } })
      ),
  });

  const stopOverlayLoading = () => setIsFullLoading(false);
  const startOverlayLoading = () => setIsFullLoading(true);

  const filters: JSX.Element[] = [];

  function getCustomerWithID(customerId: Organization["id"]) {
    return customers.filter((org) => !org.isVendor && customerId === org.id)[0];
  }

  useEffect(() => {
    if (
      profile.userOrganizationInformations.role === Role.Admin &&
      !selectedCustomer
    ) {
      if (selectedCustomerId) {
        const customer = getCustomerWithID(selectedCustomerId);
        if (customer) {
          setSelectedCustomer(customer);
        }
      } else {
        const customer = customers.filter((org) => !org.isVendor)[0];
        if (customer) {
          setSelectedCustomer(customer);
        }
      }
    }

    if (
      profile.userOrganizationInformations.role === Role.Vendor &&
      !selectedCustomer
    ) {
      if (selectedCustomerId) {
        const customer = customers.filter(
          (org) => !org.isVendor && org.id === selectedCustomerId
        )[0];
        setSelectedCustomer(customer);
      } else {
        const customer = customers.filter((org) => !org.isVendor)[0];
        setSelectedCustomer(customer);
      }
    }

    if (
      profile.userOrganizationInformations.role === Role.EndUser &&
      !selectedCustomer
    ) {
      const customer = customers.filter(
        (org) =>
          !org.isVendor &&
          org.id === profile.userOrganizationInformations.organizationId
      )[0];
      setSelectedCustomer(customer);
    }
  }, [profile, customers, selectedCustomerId]);

  if (profile.userOrganizationInformations.role >= Role.Vendor) {
    const customerFilter = (
      <Dropdown<Organization>
        key={"customer-filter"}
        selectedOption={selectedCustomer}
        options={customers}
        onOptionSelect={setSelectedCustomer}
        defaultValue
        defaultTitle={t("logoEditorCore.groupCustomer")}
        width="max"
        getOptionDisplay={(option) => option.name}
      />
    );

    filters.push(customerFilter);
  }

  function fileUploadHandler(image: File) {
    if (!selectedCustomer) {
      console.warn("no customer was selected");
      return;
    }

    if (
      !extensionsToMimeTypes(options.extensions).find((x) => x === image.type)
    ) {
      alert(t("logoEditorCore.invalidFileFormat", { fileType: image.type }));
      return;
    }
    setUploadImage(image);
    handleOpen();
  }

  const confirmationDialog = useConfirmationDialog(
    t("logoEditorCore.confirmUploadFile"),
    dialogAcceptHandler,
    dialogCancelHandler
  );
  function handleOpen() {
    confirmationDialog.open();
  }

  function dialogCancelHandler() {
    setUploadImage(null);
  }

  function dialogAcceptHandler() {
    createLogo();
  }

  // Flag REACT_APP_SUPPLIER_UNAVAILABLE_ENABLED: the API answered 503
  // supplierUnavailable (or the client-side upload timeout fired). The one
  // toast is withToasts'; here the chosen file is KEPT (uploadImage stays)
  // and the upload confirmation is re-opened, so one click on Yes retries
  // the same file - No discards it as today. Every other error is rethrown
  // untouched (flag off: always), so the chain rejects exactly as before.
  function keepUploadForRetry(error: unknown): void {
    if (!isSupplierUnavailableError(error)) throw error;
    handleOpen();
  }

  function createLogo() {
    if (!selectedCustomer) {
      console.warn("no customer was selected");
      return;
    }
    if (!uploadImage) {
      return;
    }
    if (optimisticEnabled) {
      // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: paint the user's own
      // file in the canvas right away (object URL for image/*, a file card
      // otherwise) instead of the full-screen spinner. The two API calls are
      // unchanged; only the waiting experience is.
      heldUploadRef.current = null;
      releasePreviewUrl();
      const previewUrl = canPreviewAsImage(uploadImage)
        ? URL.createObjectURL(uploadImage)
        : null;
      previewUrlRef.current = previewUrl;
      setUploadPreview({ file: uploadImage, previewUrl });
      handleCreateLogo(uploadImage, selectedCustomer.id, "New Logo")
        .then((x) => {
          // Flag REACT_APP_LOGO_KEEP_SHARP (F2): the user's own file IS the
          // artwork (nothing changed yet), so it stays the canvas bitmap
          // until the genuine high-res lands (heal) instead of giving way to
          // the response's upscaled 180px icon. pdf/ai/eps (no preview URL)
          // and a response that already carries a high-res: as today.
          // A response without a displayImage (ImagePending upload) cannot
          // carry the placeholder - then nothing is held and the object URL
          // is revoked in finally as today.
          const held =
            enviroment.logoKeepSharpEnabled &&
            previewUrl &&
            !hasGenuineHighRes(x.displayImage)
              ? withSharpPlaceholder(x, previewUrl, true)
              : x;
          if (held !== x) {
            heldUploadRef.current = {
              logoId: x.id,
              pendingPolls: 0,
              absorbSeed: false,
            };
          }
          setSelectedLogo(held);
          setLogoForm(toLogoForm(x as ImageLogo));
          setUploadImage(null);
          resetList();
          bumpHeal();
        })
        .catch(keepUploadForRetry)
        .finally(() => {
          setUploadPreview(null);
          // F2: a held file outlives the upload call (revoked on release).
          if (!heldUploadRef.current) releasePreviewUrl();
        });
      return;
    }
    startOverlayLoading();
    handleCreateLogo(uploadImage, selectedCustomer.id, "New Logo")
      .then((x) => {
        setSelectedLogo(x);
        setUploadImage(null);
        resetList();
        bumpHeal();
      })
      .catch(keepUploadForRetry)
      .finally(stopOverlayLoading);
  }

  function handleSelectLogo(logo: BaseLogo) {
    if (isLoading) {
      return;
    }
    releaseHeldUpload();
    setSelectedLogo(logo);
  }

  function handleSaveDraftChanges() {
    const updatedLogo = {
      ...selectedLogo,
      ...logoForm,
      customerId: logoForm.customer.id,
    } as ImageLogo;

    // Check if transfer method has changed
    const methodChanged =
      selectedLogo?.transferMethod?.key !== logoForm.transferMethod?.key;

    handleUpdateLogo(selectedCustomerId!, updatedLogo).then((res) => {
      // Flag REACT_APP_LOGO_KEEP_SHARP (F1): a title/customer save does not
      // change the artwork - keep the sharp canvas, take the rest of the
      // response. A method change renders new artwork: as today (the
      // response's thumbnail, then the heal), and a held upload file is no
      // longer the artwork.
      if (enviroment.logoKeepSharpEnabled && !methodChanged) {
        setSelectedLogo(withKeptSharpImage(selectedLogo, res));
        restartHeldUploadBudget();
      } else {
        setSelectedLogo(res);
        releaseHeldUpload();
      }
      setLogoForm(toLogoForm(res as ImageLogo));
      markLogoListAsStale();
      bumpHeal();

      // If method changed, automatically trigger resize with optimal size considering rotation
      if (methodChanged && logoForm.transferMethod?.size) {
        // Calculate optimal size using rotation-aware logic with the updated logo dimensions
        const updatedImageLogo = res as ImageLogo;
        const correctedSize = correctLogoSizeForTransferMethod(
          updatedImageLogo.widthMm,
          updatedImageLogo.heightMm,
          logoForm.transferMethod.size
        );

        // Only resize if the dimensions actually changed (logo doesn't fit in new method)
        const needsResize =
          correctedSize.width !== updatedImageLogo.widthMm ||
          correctedSize.height !== updatedImageLogo.heightMm;

        if (needsResize) {
          const resizeableLogo = {
            ...res,
            heightMm: correctedSize.height,
            widthMm: correctedSize.width,
          } as ImageLogo;

          handleResizeLogo(selectedCustomerId!, resizeableLogo).then(
            (resizedLogo) => {
              const resizedImageLogo = resizedLogo as ImageLogo;
              // Flag REACT_APP_LOGO_KEEP_SHARP (F1): same rule as handleResize
              // - when the method-change response carried a genuine high-res
              // (stored render for that method), the resize keeps it.
              setSelectedLogo(
                enviroment.logoKeepSharpEnabled
                  ? withKeptSharpImage(res, { ...resizedImageLogo })
                  : { ...resizedImageLogo }
              );
              setLogoForm(toLogoForm(resizedImageLogo));
              markLogoListAsStale();
              // Second mutation of the same click: restart the budget again
              // (last mutation wins - never 3 + 3).
              bumpHeal();
            }
          );
        }
      }

      navigate(`/logo/create?logo=${res.id}&organization=${res.customer.id}`);
    });
  }

  function handleSaveApproval() {
    const updatedLogo = {
      ...selectedLogo,
      ...logoForm,
      customerId: logoForm.customer.id,
    } as ImageLogo;

    handleUpdateLogo(updatedLogo.customer.id, updatedLogo).then((res) => {
      // Merge the base logo with the selected customer, as the customer is reverted in the API response.
      const baseLogo = {
        ...res,
        customerId: updatedLogo.customer.id,
      };
      setSelectedLogo(baseLogo);
      setLogoForm(toLogoForm(baseLogo as ImageLogo));
      markLogoListAsStale();
      start(baseLogo);
    });
  }

  function updateColorHandler(colors: ColorChange[]) {
    //console.log("Color changes being applied:", colors);

    if (!selectedLogo) {
      console.warn("No Logo was selected");
      return;
    }

    // Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: capture the from -> to
    // pairs NOW - LogoColorChanger clears its selection synchronously right
    // after this call - so the progress strip can show them until the
    // response lands. Untouched colours (to undefined) are dropped.
    if (optimisticEnabled) {
      setPendingColorChanges(colors.filter((c) => !!c.to));
    }

    handleUpdateLogoColor(selectedCustomerId!, selectedLogo.id, {
      colorChanges: colors,
    })
      .then((x) => {
        const imageLogo = x as ImageLogo;
        setColorChangeCode(imageLogo.colorChange?.colorChangeCode ?? null);
        setSelectedLogo({
          ...imageLogo,
          colorChange: imageLogo.colorChange,
        } as ImageLogo);
        // Flag REACT_APP_LOGO_KEEP_SHARP: new colours = new artwork; a held
        // upload file is no longer what the logo looks like.
        releaseHeldUpload();
        setLogoForm((currentForm) => ({
          ...currentForm,
          colors: imageLogo.colors,
        }));
        markLogoListAsStale();
        bumpHeal();
      })
      .finally(() => {
        if (optimisticEnabled) setPendingColorChanges(null);
      });
  }

  function cancelHandler() {
    releaseHeldUpload();
    setSelectedLogo(undefined);
    navigate("/logo");
  }

  const logoListRef = useRef<ListReset>(null);
  const resetList = () => {
    if (logoListRef.current) logoListRef.current.reset();
  };

  function toLogoForm(logo: ImageLogo): ImageLogoForm {
    return {
      id: logo.id,
      title: logo.title ?? "",
      transferMethod: logo.transferMethod,
      heightMm: logo.heightMm ?? 0,
      widthMm: logo.widthMm ?? 0,
      customer: logo.customer,
      colors: logo.colors,
    };
  }

  function updateForm(formLogo: ImageLogoForm) {
    setLogoForm({ ...formLogo });
  }

  function handleResize() {
    const updatedLogo = {
      ...selectedLogo,
      ...logoForm,
    } as ImageLogo;

    handleResizeLogo(selectedCustomerId!, updatedLogo).then((resizedLogo) => {
      const resizedImageLogo = resizedLogo as ImageLogo;
      // Flag REACT_APP_LOGO_KEEP_SHARP (F1): a resize never changes the
      // pixels (the mm live in KitMaker, the render is mm-independent), so
      // the sharp canvas stays; only mm/product from the response are taken.
      // The response's 180px thumbnail - blank in 62 % of resizes - is never
      // painted; the heal swaps in the new high-res when it lands.
      setSelectedLogo(
        enviroment.logoKeepSharpEnabled
          ? withKeptSharpImage(selectedLogo, { ...resizedImageLogo })
          : { ...resizedImageLogo }
      );
      restartHeldUploadBudget();
      setLogoForm({
        ...logoForm,
        heightMm: resizedImageLogo.heightMm,
        widthMm: resizedImageLogo.widthMm,
      });
      markLogoListAsStale();
      bumpHeal();
    });
  }

  return {
    logoListRef,
    isLoading: isFullLoading,
    isToolLoading: isFullLoading ? false : isLoading,
    onToolCancel: cancelHandler,
    onFileUploadUpload: fileUploadHandler,
    filters: filters,
    onToolSaveDraftChanges: handleSaveDraftChanges,
    onToolSaveApprovalChanges: handleSaveApproval,
    options: options,
    onSelectLogo: handleSelectLogo,
    profile: profile,
    selectedCustomer: selectedCustomer,
    selectedLogo: selectedLogo,
    uploadPreview,
    pendingColorChanges,
    onToolUpdateColor: updateColorHandler,
    approvalDialog: component,
    confirmationDialog: confirmationDialog.component,
    customers,
    logoForm: logoForm,
    updateForm: updateForm,
    onResize: handleResize,
  };
}
